import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import {
  DateRange,
  FlexColumn,
  FlexSpace,
  Header2,
  LinksContainer,
  linkTab,
  LinkTab,
  Modal
} from '@ubeya/shared-web/components';
import {
  EMPLOYEE_JOB_MONTHS_AGO,
  EMPLOYEE_JOB_MONTHS_AHEAD,
  EMPLOYEE_JOB_STATS,
  getDatesRange
} from '@ubeya/shared/constants';
import useEmployeeJobStats from '@ubeya/shared/hooks/employees/useEmployeeJobStats';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import { AccordionSummaryContainer } from '@ubeya/shared-web/components/Accordion/Accordion';
import { ReactComponent as FullScreenIcon } from '@ubeya/shared-web/assets/full-screen-open.svg';
import { ReactComponent as ExitFullScreenIcon } from '@ubeya/shared-web/assets/full-screen-close.svg';
import { trackEvent } from '@ubeya/shared-web/analytics/amplitude';
import { ReactComponent as ExternalLink } from '@ubeya/shared-web/assets/external-link.svg';
import { RootWrapper } from '../styled';
import UnbookedTable from './UnbookedTable';
import JobTimesheet from './JobTimesheet';

const LinkIcon = styled(ExternalLink)`
  margin-right: 4px;

  path {
    fill: ${({ theme }) => theme.colors.link};
  }
`;

const SectionWrapper = styled(FlexSpace)`
  margin-top: 8px;
`;

const TABS = {
  pastJobs: {
    statusFilter: 'pastJobs',
    dataKey: 'pastJobs',
    amplitudeModuleName: 'Past jobs'
  },
  futureJobs: {
    statusFilter: 'futureJobs',
    dataKey: 'futureJobs',
    amplitudeModuleName: 'Future jobs'
  },
  cancellations: { statusFilter: 'cancellations', dataKey: 'cancellations' },
  customDatesJobs: {
    statusFilter: 'customDatesJobs',
    dataKey: 'customDatesJobs',
    amplitudeModuleName: 'Custom Date Jobs'
  }
};

const Space = styled.div`
  margin-top: 16px;
`;

const Wrapper = styled(FlexColumn)`
  flex: 1;
  padding: 0 41px 40px;
  min-width: 500px;
`;

const LinksWrapper = styled(FlexSpace)`
  flex-direction: row;
  align-items: center;
`;

const ExitFullScreenIconButton = styled(ExitFullScreenIcon)`
  cursor: pointer;
`;

const PayrollLink = styled.a`
  ${linkTab};
  color: ${({ theme }) => theme.colors.link};
  margin-left: auto;
  margin-right: 0;
  display: flex;
  align-items: center;

  &:hover {
    text-decoration: underline !important;
    color: ${({ theme }) => theme.colors.link};
  }
`;

const LinkList = ({ activeTab, setActiveTab, data, employeeId }) => {
  const { t } = useContext(ConfigContext);

  const handleTabClick = useCallback(
    (tabKey) => {
      if (tabKey === activeTab) {
        return;
      }
      trackEvent('Employee card jobs tab clicked', {
        tab_name: TABS[tabKey]?.amplitudeModuleName || tabKey,
        previous_tab: TABS[activeTab]?.amplitudeModuleName || activeTab,
        employee_id: employeeId
      });
      setActiveTab(tabKey);
    },
    [activeTab, employeeId, setActiveTab]
  );

  return Object.entries(TABS).map(([key]) => (
    <LinkTab key={key} isActive={key === activeTab} onClick={() => handleTabClick(key)}>
      <div>
        {t(key)} {data && key !== 'customDatesJobs' ? `(${data[key]})` : ''}
      </div>
    </LinkTab>
  ));
};

const Job = ({ data: { id } }) => {
  const { t, accountId } = useContext(ConfigContext);
  const myPrivileges = useContext(PrivilegesContext);
  const [activeTab, setActiveTab] = useState('pastJobs');
  const [timeRange, setTimeRange] = useState(getDatesRange().thisWeek);
  const [isCardExpand, setIsCardExpand] = useState(false);

  const { data } = useEmployeeJobStats({
    employeeId: id,
    ...EMPLOYEE_JOB_STATS
  });

  const hasPayrollPrivilege = !!myPrivileges[privileges.PAYROLL];

  const currentComponent = useMemo(() => {
    const amplitudeModuleName = TABS[activeTab]?.amplitudeModuleName;

    switch (activeTab) {
      case 'pastJobs':
        return (
          <JobTimesheet
            amplitudeModuleName={amplitudeModuleName}
            total={data?.pastJobs || 0}
            timeRange={{ start: moment().subtract(EMPLOYEE_JOB_MONTHS_AGO, 'months'), end: moment().startOf('day') }}
            employeeIds={[id]}
            nameColumnDateSort
            showEmployeeRating
            addDummy={false}
            clickableDate
            defaultSort={{ sortBy: 'name', sortDirection: 'desc' }}
          />
        );

      case 'futureJobs':
        return (
          <JobTimesheet
            amplitudeModuleName={amplitudeModuleName}
            total={data?.futureJobs || 0}
            timeRange={{ start: moment().startOf('day'), end: moment().add(EMPLOYEE_JOB_MONTHS_AHEAD, 'months') }}
            employeeIds={[id]}
            nameColumnDateSort
            clickableDate
            showEmployeeRating
            addDummy={false}
          />
        );

      case 'customDatesJobs':
        return (
          <>
            <SectionWrapper>
              <DateRange value={timeRange} onChange={setTimeRange} numberOfMonths={1} />
            </SectionWrapper>

            <JobTimesheet
              amplitudeModuleName={amplitudeModuleName}
              timeRange={timeRange}
              showEmployeeRating
              employeeIds={[id]}
              addDummy
            />
          </>
        );

      case 'cancellations':
        return (
          <SectionWrapper>
            <UnbookedTable employeeId={id} total={data?.cancellations || 0} />
          </SectionWrapper>
        );

      default:
        return null;
    }
  }, [activeTab, data?.cancellations, data?.futureJobs, data?.pastJobs, id, timeRange]);

  const handleCardExpand = useCallback(() => {
    setIsCardExpand(true);
  }, []);

  const onClose = useCallback(() => {
    setIsCardExpand(false);
  }, []);

  const Content = useMemo(
    () => (
      <>
        <LinksWrapper>
          <LinksContainer>
            <LinkList data={data} activeTab={activeTab} setActiveTab={setActiveTab} employeeId={id} />
          </LinksContainer>

          {hasPayrollPrivilege && (
            <PayrollLink href={`/${accountId}/payroll?employeeId=${id}`} target="_blank" rel="noopener noreferrer">
              <LinkIcon />
              {t('employeesPayrollLink')}
            </PayrollLink>
          )}

          {isCardExpand && <ExitFullScreenIconButton onClick={onClose} />}
        </LinksWrapper>

        {currentComponent}
      </>
    ),
    [data, activeTab, isCardExpand, onClose, currentComponent, id, hasPayrollPrivilege, accountId, t]
  );

  return (
    <RootWrapper>
      <Wrapper>
        <AccordionSummaryContainer>
          <Header2> {t('jobs')} </Header2>
          <FullScreenIcon onClick={handleCardExpand} />
        </AccordionSummaryContainer>
        {isCardExpand ? (
          <Modal onClose={onClose} showConfirmButton={false} size="xxlarge">
            {Content}
          </Modal>
        ) : (
          <>
            <Space />
            {Content}
          </>
        )}
      </Wrapper>
    </RootWrapper>
  );
};

export default React.memo(Job);
